import { axiosInstance } from "@/utils/axios";
import type { ScoringProfile, ScoringProfileCreate } from "@/lib/scoring/types";

const BASE_URL = "/niffler-api/scoringCriteria";

// Niffler stores `parameters` as a JSON string on the wire
interface ScoringProfileRaw extends Omit<ScoringProfile, "parameters"> {
  parameters: string | ScoringProfile["parameters"];
}

interface FetchResponse {
  meta: { total: number };
  data: ScoringProfileRaw[];
}

function parseParameters(
  raw: string | ScoringProfile["parameters"]
): ScoringProfile["parameters"] | null {
  if (typeof raw !== "string") return raw;
  try {
    return JSON.parse(raw) as ScoringProfile["parameters"];
  } catch {
    return null;
  }
}

function hydrate(raw: ScoringProfileRaw): ScoringProfile | null {
  const parameters = parseParameters(raw.parameters);
  if (!parameters) return null;
  return { ...raw, parameters };
}

export async function fetchScoringProfiles(
  email: string
): Promise<ScoringProfile[]> {
  const { data } = await axiosInstance.get<FetchResponse>(BASE_URL, {
    params: { email, isSpog: true, paginate: false, sort: "name" },
  });
  return data.data.map(hydrate).filter((p): p is ScoringProfile => p !== null);
}

function serializeProfile(
  profile: ScoringProfileCreate | ScoringProfile
): unknown {
  return {
    ...profile,
    parameters: JSON.stringify(
      {
        version: profile.parameters.version ?? 1,
        criteria: profile.parameters.criteria,
        scoringMethod: profile.parameters.scoringMethod,
        localizeInstalls: profile.parameters.localizeInstalls,
      },
      (key, value) => (key === "__typename" ? undefined : value)
    ),
  };
}

export async function createScoringProfile(
  profile: ScoringProfileCreate
): Promise<ScoringProfile> {
  const { data } = await axiosInstance.post<ScoringProfileRaw>(
    BASE_URL,
    serializeProfile(profile)
  );
  const result = hydrate(data);
  if (!result) throw new Error("Failed to parse created profile response");
  return result;
}

export async function updateScoringProfile(
  profile: ScoringProfile
): Promise<ScoringProfile> {
  const { data } = await axiosInstance.put<ScoringProfileRaw>(
    BASE_URL,
    serializeProfile(profile)
  );
  const result = hydrate(data);
  if (!result) throw new Error("Failed to parse updated profile response");
  return result;
}

export async function deleteScoringProfile(id: number): Promise<void> {
  await axiosInstance.delete(`${BASE_URL}/${id}`);
}
